<script>
	import { createEventDispatcher, getContext, onMount } from 'svelte';

	import { keyToken } from './globals.js';
	import { formatPercent } from './components/formatting.js';
	import SortableTable from './components/table/SortableTable.svelte';
	import StockTableCell from './components/table/StockTableCell.svelte';

	const dispatch = createEventDispatcher();

	const { getToken } = getContext(keyToken);
	const token = getToken();

	export let updates;

	let invalid;
	let stocks = [];
	let stock = null;
	let averageDayDeltaPercent;
	let averageYearDeltaPercent;
	let averageDividendPercent;

	$: {
		if (updates != null && (updates.type === 'watchlist' || updates.type === 'prices')) {
			console.log('Received watchlist update:', updates);
			loadWatchlist();
		}
	}

	async function loadWatchlist() {
		if (!token) {
			invalid = true;
			return;
		}

		await fetch('https://ostium.ch/api/borsalino/get_watchlist.php', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=utf-8',
				'Authorization': 'JWT ' + encodeURIComponent(token)
			}
		}).then(result => {
			if (result.ok) {
				console.log('Loaded watchlist data successfully.');
				invalid = false;
				result.json().then(data => {
					console.log(`Loaded ${data.length} watchlist record${data.length === 1 ? '' : 's'}.`);
					averageYearDeltaPercent = 0;
					averageDividendPercent = 0;
					stocks = data.map(stock => {
						stock.close_day_delta_percent = stock.last / stock.close_day - 1;
						stock.close_year_delta_percent = stock.last / stock.close_year - 1;
						stock.dividend_percent = stock.dividend_amount != null && stock.dividend_date != null ? stock.dividend_amount / stock.last : null;
						averageDayDeltaPercent += stock.close_day_delta_percent;
						averageYearDeltaPercent += stock.close_year_delta_percent;
						averageDividendPercent += stock.dividend_percent;
						return stock;
					});
					averageDayDeltaPercent /= data.length || 1;
					averageYearDeltaPercent /= data.length || 1;
					averageDividendPercent /= data.length || 1;
				});
			} else {
				invalid = true;
				console.warn('Could not load watchlist data.', result.statusText);
			}
		});
	}

	async function deleteFromWatchlist() {
		if (!token) {
			console.warn('Not logged in!');
			return;
		}

		await fetch('https://ostium.ch/api/borsalino/delete_watchlist.php', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=utf-8',
				'Authorization': 'JWT ' + encodeURIComponent(token)
			},
			body: JSON.stringify({ id: stock.watchlist_id, isin: stock.isin })
		}).then(result => {
			if (result.ok) {
				result.json().then(data => {
					console.log('Deleted asset from watchlist:', data);
					dispatch('message', {
						type: 'success',
						text: data.message
					});
				});
			} else {
				console.warn('Could not delete asset from watchlist.', result.statusText);
				result.json().then(data => {
					console.log('Error:', data);
					dispatch('message', {
						type: 'error',
						text: data.error != null ? data.error : data.warning
					});
				});
			}
			jQuery('#deleteWatchlistDialog').modal('hide');
			loadWatchlist();
		});
	}

	async function updatePrice(event) {
		if (!token) {
			console.warn('Not logged in!');
			return;
		}

		let target =  event.target;
		if (target != null && target.dataset != null && target.dataset['wid'] != null) {
			let wid = target.dataset['wid'];
			stock = stocks.find(item => item.watchlist_id == wid);
			console.log('Update price of stock: ', stock);
			await fetch('https://ostium.ch/api/borsalino/update_index.php', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json;charset=utf-8',
					'Authorization': 'JWT ' + encodeURIComponent(token)
				},
				body: JSON.stringify({ symbol: stock.symbol })
			}).then(result => {
				if (result.ok) {
					result.json().then(data => {
						console.log('Updated price of asset:', data);
					});
				} else {
					console.warn('Could not update price of asset.', result.statusText);
					result.json().then(data => {
						console.log('Error:', data);
						dispatch('message', {
							type: 'error',
							text: data.error != null ? data.error : data.warning
						});
					});
				}
				loadWatchlist();
			});
		}
	}

	onMount(() => {
		jQuery('#deleteWatchlistDialog').on('show.bs.modal', function (event) {
			let target =  event.relatedTarget;
			if (target != null && target.dataset != null && target.dataset['wid'] != null) {
				let wid = target.dataset['wid'];
				stock = stocks.find(item => item.watchlist_id == wid);
				console.log('Delete watchlist with ID ' + wid, stock);
			}
		});
		loadWatchlist();
	});
</script>

<div class="container-fluid">
	<div class="row">
		<div class="col">
			<h2 class="mt-4 mb-2"><a class="text-light" href="#collapseWatchlist" data-toggle="collapse" aria-expanded="true">Watchlist</a></h2>

			<SortableTable id="collapseWatchlist" class="table table-sm table-dark table-striped" containerClass="collapse show" headClass="bg-secondary" footClass="bg-secondary" items={stocks}>
				<tr slot="thead">
					<th data-sort="name" data-sort-initial="ascending">Name</th>
					<th class="hide-md" data-sort="symbol">Symbol</th>
					<th class="hide-lg" data-sort="isin">ISIN</th>
					<th data-sort="currency">Currency</th>
					<th class="text-right" data-sort="last">Last</th>
					<th class="text-right" data-sort="close_day_delta_percent">Diff %</th>
					<th class="text-right" data-sort="close_year_delta_percent">YTD %</th>
					<th class="text-right" data-sort="dividend_percent">Dividend %</th>
					<th></th>
				</tr>
				<tr slot="tbody" let:item={stock}>
					<StockTableCell {stock} show='name' href="." target="#assetDetailsDialog"/>
					<StockTableCell {stock} show='symbol' class="hide-md" href="." target="#assetDetailsDialog"/>
					<StockTableCell {stock} show='isin' class="hide-lg" href="." target="#assetDetailsDialog"/>
					<StockTableCell {stock} show='currency'/>
					<StockTableCell {stock} show='last'/>
					<StockTableCell {stock} show='close_day_delta_percent'/>
					<StockTableCell {stock} show='close_year_delta_percent'/>
					<StockTableCell {stock} show='dividend_percent'/>
					<td class="col-action">
						<div class="btn-group" role="group">
							<button type="button" class="btn btn-link text-light fas fa-ellipsis-v" data-toggle="dropdown"></button>
							<div class="dropdown-menu dropdown-menu-right">
								<a href="." class="dropdown-item" data-toggle="modal" data-target="#deleteWatchlistDialog" data-wid={stock.watchlist_id}>Delete</a>
								<a href="." class="dropdown-item" on:click|preventDefault={updatePrice} data-wid={stock.watchlist_id}>Update Price</a>
							</div>
						</div>
					</td>
				</tr>
				<tr slot="tfoot">
					<th><a href="." class="text-light" data-toggle="modal" data-target="#assetDetailsDialog" data-isin="WATCHLIST">Average</a></th>
					<th class="hide-lg"></th>
					<th></th>
					<th></th>
					<th></th>
					<th class="{'text-right ' + (averageDayDeltaPercent < 0 ? 'text-danger' : 'text-success')}">{formatPercent(averageDayDeltaPercent)}</th>
					<th class="{'text-right ' + (averageYearDeltaPercent < 0 ? 'text-danger' : 'text-success')}">{formatPercent(averageYearDeltaPercent)}</th>
					<th class="{'text-right ' + (averageDividendPercent < 0 ? 'text-danger' : 'text-success')}">{formatPercent(averageDividendPercent)}</th>
					<th></th>
				</tr>
			</SortableTable>
		</div>
	</div>
</div>

<div class="modal fade" id="deleteWatchlistDialog" tabindex="-1" role="alert">
	<div class="modal-dialog modal-dialog-centered modal-lg" role="document">
		<div class="modal-content">
			<div class="modal-header bg-warning">
				<h5 class="modal-title text-dark">Caution</h5>
				<button type="button" class="close" data-dismiss="modal" aria-label="Close">
					<span aria-hidden="true">&times;</span>
				</button>
			</div>
			<div class="modal-body text-dark">
				{#if stock != null}
				Do you really want to remove {stock.name} ({stock.isin}) from the watchlist?
				{/if}
			</div>
			<div class="modal-footer">
				<button type="button" class="btn btn-danger" data-dismiss="modal">No, keep it!</button>
				<button type="button" class="btn btn-success" on:click|preventDefault={deleteFromWatchlist}>Yes, delete it.</button>
			</div>
		</div>
	</div>
</div>
